<template>
  <div class="auth-split-layout">
    <div class="auth-split-left">
      <div class="auth-brand">
        <img src="/favicon.png" alt="" />
        <span class="auth-brand-name">RabiiTrip</span>
      </div>
      <h1>旅のすべてを、みんなの共同作業に。</h1>
      <p class="auth-tagline">
        安全のため、定期的なパスワード変更をおすすめします。
      </p>
    </div>
    <div class="auth-split-right">
      <div class="reset-password-container">
        <div class="reset-password-card">
          <div class="logo-section">
            <h1 class="app-title">RabiiTrip</h1>
            <p class="subtitle">パスワードリセット</p>
          </div>

      <!-- トークン検証中 -->
      <div v-if="isVerifying" class="loading-section">
        <div class="spinner"></div>
        <p>トークンを確認中...</p>
      </div>

      <!-- トークンエラー -->
      <div v-else-if="tokenError" class="error-section">
        <div class="error-icon">⚠️</div>
        <h2>無効なリンク</h2>
        <p class="error-message">{{ tokenError }}</p>
        <button @click="goToLogin" class="btn btn-primary">
          ログインページへ
        </button>
      </div>

      <!-- パスワードリセットフォーム -->
      <div v-else class="form-section">
        <form @submit.prevent="handleResetPassword">
          <div class="form-group">
            <label class="form-label">新しいパスワード</label>
            <div class="password-input-wrapper">
              <input
                v-model="newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                class="form-input"
                placeholder="新しいパスワードを入力（6文字以上）"
                required
                minlength="6"
                @paste.prevent
                @copy.prevent
              />
              <button
                type="button"
                class="password-toggle-btn"
                @click="showNewPassword = !showNewPassword"
              >
                <svg
                  v-if="!showNewPassword"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                  ></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">新しいパスワード（確認）</label>
            <div class="password-input-wrapper">
              <input
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-input"
                placeholder="新しいパスワードを再入力"
                required
                @paste.prevent
                @copy.prevent
              />
              <button
                type="button"
                class="password-toggle-btn"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <svg
                  v-if="!showConfirmPassword"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                  ></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
          <p v-if="successMessage" class="success-text">{{ successMessage }}</p>

          <button
            type="submit"
            class="btn btn-primary btn-full"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? "変更中..." : "パスワードを変更" }}
          </button>
        </form>

        <div class="footer-links">
          <a href="/login" class="link">ログインページへ戻る</a>
        </div>
      </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import axios from "axios";
import { API_URL } from "@/config/api.js";

const router = useRouter();
const route = useRoute();

const isVerifying = ref(true);
const tokenError = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref("");
const successMessage = ref("");
const resetToken = ref("");

// トークンを検証
const verifyToken = async () => {
  const token = route.query.token;

  if (!token) {
    tokenError.value =
      "無効なリンクです。パスワードリセットのリクエストを再度行ってください。";
    isVerifying.value = false;
    return;
  }

  resetToken.value = token;

  try {
    // トークンの有効性をバックエンドで検証（オプション）
    // 現時点では単にトークンが存在すればOK
    isVerifying.value = false;
  } catch (error) {
    console.error("Token verification error:", error);
    tokenError.value = "トークンの検証に失敗しました。";
    isVerifying.value = false;
  }
};

// パスワードリセット処理
const handleResetPassword = async () => {
  errorMessage.value = "";
  successMessage.value = "";

  if (!newPassword.value || !confirmPassword.value) {
    errorMessage.value = "すべての項目を入力してください";
    return;
  }

  if (newPassword.value.length < 6) {
    errorMessage.value = "パスワードは6文字以上にしてください";
    return;
  }

  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = "パスワードが一致しません";
    return;
  }

  isSubmitting.value = true;

  try {
    await axios.post(`${API_URL}/auth/password-reset/confirm`, {
      token: resetToken.value,
      new_password: newPassword.value,
    });

    successMessage.value =
      "パスワードを変更しました。ログインページに移動します...";

    // 3秒後にログインページへ遷移
    setTimeout(() => {
      router.push("/login");
    }, 3000);
  } catch (error) {
    console.error("Password reset error:", error);

    if (error.response?.data?.detail) {
      errorMessage.value = error.response.data.detail;
    } else {
      errorMessage.value =
        "パスワードのリセットに失敗しました。リンクが期限切れの可能性があります。";
    }
  } finally {
    isSubmitting.value = false;
  }
};

// ログインページへ遷移
const goToLogin = () => {
  router.push("/login");
};

onMounted(() => {
  verifyToken();
});
</script>

<style scoped>
.reset-password-container {
  min-height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface);
  padding: 20px;
  width: 100%;
}

.reset-password-card {
  background: var(--color-surface);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 40px;
  max-width: 450px;
  width: 100%;
}

.logo-section {
  text-align: center;
  margin-bottom: 30px;
}

.app-title {
  font-size: 32px;
  font-weight: bold;
  color: #B4CF9E;
  margin: 0;
}

.subtitle {
  color: #666;
  font-size: 16px;
  margin-top: 8px;
}

.loading-section,
.error-section {
  text-align: center;
  padding: 20px 0;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #B4CF9E;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-icon {
  font-size: 60px;
  margin-bottom: 20px;
}

.error-section h2 {
  color: #333;
  margin-bottom: 10px;
}

.error-message {
  color: #666;
  margin-bottom: 30px;
  line-height: 1.6;
}

.form-section {
  margin-top: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-input {
  width: 100%;
  padding: 12px 40px 12px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #B4CF9E;
}

.password-input-wrapper {
  position: relative;
}

.password-toggle-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle-btn:hover {
  color: #B4CF9E;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: linear-gradient(135deg, #B4CF9E 0%, #C5DCB3 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-full {
  width: 100%;
  margin-top: 10px;
}

.error-text {
  color: #e74c3c;
  font-size: 14px;
  margin: 10px 0;
  text-align: center;
}

.success-text {
  color: #27ae60;
  font-size: 14px;
  margin: 10px 0;
  text-align: center;
  font-weight: 500;
}

.footer-links {
  margin-top: 20px;
  text-align: center;
}

.link {
  color: #B4CF9E;
  text-decoration: none;
  font-size: 14px;
}

.link:hover {
  text-decoration: underline;
}
</style>
